import React, { FC, useEffect } from 'react';
import { Spin, message } from 'antd';
import { useParams } from 'react-router-dom';
import { ESocketEvent } from 'common/const/enum';
import { useAppDispatch, useAppSelector, useNotification, useSocket } from 'common/hooks';
import { IEvent } from 'entities/Events/Event.models';
import { getEventAvailableOperations } from 'entities/Events/Event.helper';
import { EEventStatus } from 'entities/Events/Event.enum';
import { EventContent, EventHeader, EventRouterHandler, EventSidebar } from 'entities/Events/components';

export const Event: FC = () => {
  const { auth, event, eventLoading, patient, patientLoading, timer, timerIsRunning } = useAppSelector((state) => ({
    auth: state.auth.data,
    event: state.event.data,
    eventLoading: state.event.loading,
    patient: state.patient.data,
    patientLoading: state.patient.loading,
    timer: state.timer.data,
    timerIsRunning: state.timer.isRunning,
  }));
  const { event: eventDispatch, patient: patientDispatch, timer: timerDispatch } = useAppDispatch();
  const { id } = useParams();

  useEffect(() => {
    return () => {
      eventDispatch.clearEvent();
    };
  }, []);

  useEffect(() => {
    if (!event) {
      eventDispatch.getEventById(eventId);
    }
  }, [event]);

  useEffect(() => {
    const fetch = async () => {
      if (event && !event.blockRequests) {
        await patientDispatch.getPatientByEventId(eventId);
        timerDispatch.getTimer(eventId);
      }
    };

    fetch();
  }, [event]);

  const { newEventNotificationContext, showNewEventNotification } = useNotification();

  const eventId = Number(id);
  const currentUserId = auth?.access?.id;
  const role = auth?.access?.rights[0];

  const { blockRouter } = getEventAvailableOperations({
    status: event?.status,
    type: event?.type,
    role,
    currentUserId,
    staffId: event?.staffId,
    physicianId: event?.physicianId,
  });

  useSocket(
    ESocketEvent.Events,
    async (eventFromNotification: IEvent) => {
      if (event && eventFromNotification.id !== event.id && eventFromNotification.status === EEventStatus.Inbox) {
        showNewEventNotification();
      }

      if (
        event &&
        eventFromNotification.id === event.id &&
        (eventFromNotification.status === EEventStatus.Completed || eventFromNotification.status === EEventStatus.Inbox)
      ) {
        if (timerIsRunning) {
          const response = await timerDispatch.stopTimer({ eventId: event.id });

          if (response && timer) {
            timerDispatch.setTimer({ ...timer, time: response });
          }
        }

        if (eventFromNotification.status === EEventStatus.Completed) {
          message.info('Event is completed');
        }

        eventDispatch.setEvent({ ...eventFromNotification, blockRequests: true });
      }
    },
    [event, patient, currentUserId, timer, timerIsRunning],
  );

  return (
    <div className="event">
      <div className="mb-8">
        <EventHeader type={event?.type} />
      </div>

      <Spin spinning={eventLoading || patientLoading}>
        <div className="d-flex event__container">
          <EventContent event={event} patient={patient} />

          <EventSidebar event={event} patient={patient} />
        </div>
      </Spin>

      <EventRouterHandler eventId={eventId} blockRouter={blockRouter} />

      {newEventNotificationContext}
    </div>
  );
};
